import { useEffect } from 'react';
import { useBlocker } from 'react-router';
import { Alert, Box, CircularProgress, MenuItem, Paper, TextField } from '@mui/material';
import { RefreshCwIcon } from 'lucide-react';

import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/credit-conversion-settings/ConfirmDialog';
import { EntryListPanel } from '@/components/credit-conversion-settings/EntryListPanel';
import { isDirty } from '@/components/credit-conversion-settings/editorReducer';
import {
  FROZEN_NOTICE,
  useConversionSettings,
} from '@/components/credit-conversion-settings/useConversionSettings';
import { ValueEditor } from '@/components/credit-conversion-settings/ValueEditor';
import { ValueView } from '@/components/credit-conversion-settings/ValueView';
import { settingItems, type SettingItemKey } from '@/lib/credit-conversion-settings/tables';

/**
 * 換算設定（FKV3-13044・FKV3-13220）。サイドバー「クレジット制」>「換算設定」。
 * credit DB の換算の設定値のうち AWS単価を除く7テーブルを、6つの設定項目に分けて、適用開始日ごとに参照・追加・編集・削除する。
 */
export function ConversionSettingsPage() {
  const s = useConversionSettings();
  const editing = s.editor !== null;
  const dirty = s.editor !== null && isDirty(s.editor);

  // 入力を変更した後に、ほかの画面に移動するとき（この画面を開き直すときを含む）・タブを閉じる・リロードするときは、
  // 破棄してよいかを確認する
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirty &&
      (currentLocation.pathname !== nextLocation.pathname ||
        currentLocation.search !== nextLocation.search),
  );
  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const dateOfAction = s.editor?.date || (s.value.status === 'ok' ? s.value.data.effective_dt : '');

  return (
    <AppLayout title="換算設定" showSearch={false}>
      <Box
        display="flex"
        flexDirection="column"
        gap={2}
        sx={{ width: '90%', maxWidth: 1100, mx: 'auto', px: 2 }}
      >
        <TextField
          select
          size="small"
          label="設定項目"
          value={s.item.key}
          onChange={(e) => s.selectItem(e.target.value as SettingItemKey)}
          disabled={editing || s.busy || s.frozen}
          sx={{ width: 320 }}
        >
          {settingItems.map((i) => (
            <MenuItem key={i.key} value={i.key}>
              {i.label}
            </MenuItem>
          ))}
        </TextField>

        <EntryListPanel
          list={s.list}
          selectedDate={s.selectedDate}
          disabled={editing || s.busy || s.frozen}
          valueLoading={s.value.status === 'loading'}
          onSelect={s.selectDate}
          onCreate={() => void s.startCreate()}
          onReload={s.reload}
        />

        {s.notice && <Alert severity="info">{s.notice}</Alert>}

        <Paper variant="outlined" sx={{ p: 2 }}>
          {s.editor ? (
            <ValueEditor
              editor={s.editor}
              failure={s.failure}
              busy={s.busy}
              frozen={s.frozen}
              dispatch={s.dispatch}
              onSave={s.requestSave}
              onCancel={s.cancel}
              onReload={s.reload}
            />
          ) : (
            <ValueArea s={s} />
          )}
        </Paper>
      </Box>

      <ConfirmDialog
        open={s.confirmation === 'save'}
        title="保存しますか？"
        description={`${s.item.label}の、適用開始日 ${dateOfAction} の値を保存します。保存した値は、適用開始日から顧客の消費の換算に使われます。`}
        confirmLabel="保存する"
        onConfirm={() => void s.save()}
        onCancel={s.closeConfirmation}
      />
      <ConfirmDialog
        open={s.confirmation === 'delete'}
        title="削除しますか？"
        description={`${s.item.label}の、適用開始日 ${dateOfAction} の値を、すべての行とともに削除します。`}
        confirmLabel="削除する"
        onConfirm={() => void s.remove()}
        onCancel={s.closeConfirmation}
      />
      <ConfirmDialog
        open={s.confirmation === 'discard'}
        title="入力内容を破棄しますか？"
        description="変更した入力内容は保存されません。"
        confirmLabel="破棄する"
        onConfirm={s.discard}
        onCancel={s.closeConfirmation}
      />
      <ConfirmDialog
        open={blocker.state === 'blocked'}
        title="入力内容を破棄して移動しますか？"
        description="変更した入力内容は保存されません。"
        confirmLabel="破棄して移動する"
        onConfirm={() => blocker.proceed?.()}
        onCancel={() => blocker.reset?.()}
      />
    </AppLayout>
  );
}

/** 表示モードの値の表示領域 */
function ValueArea({ s }: { s: ReturnType<typeof useConversionSettings> }) {
  const reloadButton = (
    <Button size="sm" variant="outline" onClick={s.reload} disabled={s.busy}>
      <RefreshCwIcon />
      再読み込み
    </Button>
  );
  return (
    <>
      {/* 削除できたかが分からない旨を表示した後は、どの失敗の後も「再読み込み」で取得し直せるようにする */}
      {(s.failure || s.frozen) && (
        <Alert
          severity={s.failure ? 'error' : 'warning'}
          sx={{ mb: 2 }}
          action={s.failure?.reload || s.frozen ? reloadButton : undefined}
        >
          {s.failure ? s.failure.message : FROZEN_NOTICE}
        </Alert>
      )}
      {(s.value.status === 'loading' || (s.busy && s.value.status !== 'ok')) && (
        <Box display="flex" justifyContent="center" py={3}>
          <CircularProgress size={28} />
        </Box>
      )}
      {s.value.status === 'error' && (
        <Alert severity="error" action={reloadButton}>
          {s.value.message}
        </Alert>
      )}
      {s.value.status === 'idle' && s.list.status === 'ok' && (
        <Box color="text.secondary" fontSize={14}>
          表示する値がありません。「追加」から値を追加してください。
        </Box>
      )}
      {s.value.status === 'ok' && (
        <ValueView
          item={s.item}
          value={s.value.data}
          busy={s.busy}
          frozen={s.frozen}
          onEdit={() => void s.startEdit()}
          onDelete={s.requestDelete}
        />
      )}
    </>
  );
}
